import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project, PunchItem, Sub } from '../types'
import { fmtDate } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const STATUS_COLOR: Record<PunchItem['status'], BadgeColor> = {
  open: 'red',
  sub_notified: 'gold',
  resolved: 'gold',
  verified: 'green',
}

export default function PunchTab({ project }: { project: Project }) {
  const [items, setItems] = useState<PunchItem[]>([])
  const [subs, setSubs] = useState<Sub[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<PunchItem>>({})

  useEffect(() => {
    api.punch.list(project.id).then(setItems)
    api.subs.list().then(setSubs)
  }, [project.id])

  const grouped = useMemo(() => {
    const open = items.filter((i) => i.status === 'open' || i.status === 'sub_notified')
    const resolved = items.filter((i) => i.status === 'resolved' || i.status === 'verified')
    return { open, resolved }
  }, [items])

  async function add() {
    if (!draft.description) return
    const item = await api.punch.create(project.id, draft)
    setItems((p) => [item, ...p])
    setAdding(false)
    setDraft({})
  }

  async function setStatus(id: string, status: PunchItem['status']) {
    const updated = await api.punch.update(id, { status })
    setItems((p) => p.map((x) => (x.id === id ? updated : x)))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Punch List</h2>
          <p className="muted small">
            {grouped.open.length} open · {grouped.resolved.length} resolved
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Item'}
        </button>
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Description</span>
            <input
              value={draft.description ?? ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Drywall ding behind master bedroom door"
            />
          </label>
          <label className="form-row">
            <span>Location</span>
            <input
              value={draft.location ?? ''}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              placeholder="Master bedroom"
            />
          </label>
          <label className="form-row">
            <span>Responsible sub</span>
            <select
              value={draft.responsible_sub_id ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, responsible_sub_id: e.target.value || null })
              }
            >
              <option value="">— select —</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.company_name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Blocks payment?</span>
            <input
              type="checkbox"
              checked={draft.blocks_payment === 1}
              onChange={(e) => setDraft({ ...draft, blocks_payment: e.target.checked ? 1 : 0 })}
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add Item
          </button>
        </div>
      )}

      {[...grouped.open, ...grouped.resolved].map((i) => (
        <div key={i.id} className="card mb-2">
          <div className="row-between">
            <div>
              <div className="row-title">{i.description}</div>
              <div className="muted small">
                {i.location ?? '—'} · {i.responsible_sub_name ?? 'no sub'} ·{' '}
                {i.date_identified ? fmtDate(i.date_identified) : 'no date'}
              </div>
              {i.blocks_payment === 1 && <Badge color="red">BLOCKS PAYMENT</Badge>}
            </div>
            <div className="text-right">
              <Badge color={STATUS_COLOR[i.status]}>{i.status.toUpperCase().replace('_', ' ')}</Badge>
            </div>
          </div>
          {i.status !== 'verified' && (
            <div className="row gap-2 mt-2">
              {i.status === 'open' && (
                <button
                  className="btn-ghost small"
                  onClick={() => setStatus(i.id, 'sub_notified')}
                >
                  Sub notified
                </button>
              )}
              {i.status === 'sub_notified' && (
                <button className="btn-ghost small" onClick={() => setStatus(i.id, 'resolved')}>
                  Resolved
                </button>
              )}
              {i.status === 'resolved' && (
                <button className="btn-ghost small" onClick={() => setStatus(i.id, 'verified')}>
                  Verified
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </section>
  )
}
